import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { pick } from 'ramda';

import * as Yup from 'yup';
import { composeSchema, isPhoneNumber } from 'utils/validations';

import FmcForm from 'components/FmcForm';
import MobileVerificationView from './MobileVerificationView';
import useNavigationHelpers from 'hooks/useNavigationHelpers';
import useTranslate from 'hooks/useTranslate';
import {
  mergeApplication,
  selectCreditApplication,
} from 'state/creditApplication';
import useOTPSender, { OTPSenderError } from 'hooks/requests/otpSender';
import catchOnly from 'utils/catchOnly';
import { NonClientError } from 'hooks/requests/fetchUtils';
import { useSnackbar } from 'notistack';

export interface FormValues {
  mobilePhone: string | null;
}

type PropTypes = RouteComponentProps;

const MobileVerification: React.FC<PropTypes> = () => {
  const { push } = useNavigationHelpers();
  const t = useTranslate();
  const dispatch = useDispatch();
  const creditApplication = useSelector(selectCreditApplication);
  const [sendOTP] = useOTPSender();
  const { enqueueSnackbar } = useSnackbar();
  const schema = useMemo(
    () =>
      Yup.object().shape({
        mobilePhone: composeSchema(
          Yup.string().label(t('formLabel.mobilePhone')),
          Yup.string()
            .required()
            .nullable(),
          isPhoneNumber,
        ),
      }),
    [t],
  );

  const onSubmit = useCallback(
    async (rawValues: FormValues) => {
      const values = schema.cast(rawValues);
      dispatch(
        mergeApplication({
          mobilePhone: values.mobilePhone,
        }),
      );

      try {
        await sendOTP({ phone: values.mobilePhone as string });
        push('/unified/otp-verification');
      } catch (e) {
        if (catchOnly(e, NonClientError, OTPSenderError)) {
          enqueueSnackbar('Something has gone wrong. Please try again later.', {
            variant: 'error',
          });
        }
      }
    },
    [schema, dispatch, push, sendOTP, enqueueSnackbar],
  );

  const initialValues = pick(['mobilePhone'], creditApplication);

  return (
    <FmcForm
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={schema}
    >
      {formProps => <MobileVerificationView {...formProps} />}
    </FmcForm>
  );
};

export default MobileVerification;
